import en from '@/lang/en_EN.json';
import es from '@/lang/es_LA.json';
import pt from '@/lang/pt_BR.json';
import { generateSchema } from './generateSchemas';
import it from '@/lang/it_IT.json';
import { lockerLocations } from '@/data/lockerLocations';

const baseUrl = process.env.VUE_APP_URL;

const langMap = {
  es: 'guarderia-equipaje',
  en: 'luggage-storage',
  pt: 'guarda-volumes',
  it: 'deposito-bagagli',
};

const langs = Object.keys(langMap);

// 🔤 Utilidad: obtener traducciones según idioma
function getTextsFromTranslationFile(subArray, lang) {
  let file = es;
  if (lang === 'en') file = en;
  if (lang === 'pt') file = pt;
  if (lang === 'it') file = it;
  return file[subArray];
}

// 🧾 Meta tags dinámicos (title + description)
function getMetaDescriptions(lang, name, path) {
  let title = '';
  let description = '';

  const cities = getTextsFromTranslationFile('cities', lang);
  const cityHomeTexts = getTextsFromTranslationFile('city_home', lang);
  const LockerHomeTexts = getTextsFromTranslationFile('locker_home', lang);
  const routerTexts = getTextsFromTranslationFile('router', lang);

  if (name.includes('LockerHome')) {
    const locationId = path.split('/').filter(Boolean)[2];
    try {
      const location = lockerLocations.find((l) => l.id === locationId);
      const locationName = location ? location.name : '';
      title = LockerHomeTexts.meta_title.replace(/XXX/g, locationName);
      description = LockerHomeTexts.meta_description.replace(/XXX/g, locationName);
    } catch (e) {
      title = routerTexts.default.title;
      description = routerTexts.default.description;
    }
  } else if (name.includes('CityHome')) {
    const cityId = path.split('/').filter(Boolean)[2];
    try {
      const cityName = cities[cityId];
      title = cityHomeTexts.meta_title.replace(/XXX/g, cityName);
      description = cityHomeTexts.meta_description.replace(/XXX/g, cityName);
    } catch (e) {
      title = routerTexts.default.title;
      description = routerTexts.default.description;
    }
  } else {
    try {
      title = routerTexts[name].title;
      description = routerTexts[name].description;
    } catch (e) {
      title = routerTexts.default.title;
      description = routerTexts.default.description;
    }
  }

  const meta = [
    { name: 'description', content: description },
    { property: 'og:description', content: description },
  ];

  return { title, meta };
}

// 🌍 Hreflang dinámico
function addHreflangTag(lang, href) {
  const link = document.createElement('link');
  link.setAttribute('rel', 'alternate');
  link.setAttribute('hreflang', lang);
  link.setAttribute('href', href);
  document.head.appendChild(link);
}

function generateHreflangLinks(currentLang, path, routeName) {
  // Limpiar todos los <link rel="alternate">
  document.querySelectorAll("link[rel='alternate'][hreflang]").forEach((el) => el.remove());

  if (routeName && routeName.includes('LockerHome')) {
    const parts = path.split('/').filter(Boolean);
    const locationSlug = parts[2];

    langs.forEach((lang) => {
      const href = `${baseUrl}/${lang}/lockers/${locationSlug}/`;
      addHreflangTag(lang, href);
    });

    addHreflangTag('x-default', `${baseUrl}/es/lockers/${locationSlug}/`);
  } else if (routeName && routeName.includes('CityHome')) {
    const parts = path.split('/').filter(Boolean);
    const citySlug = parts[2];

    langs.forEach((lang) => {
      const href = `${baseUrl}/${lang}/${langMap[lang]}/${citySlug}/`;
      addHreflangTag(lang, href);
    });

    addHreflangTag('x-default', `${baseUrl}/en/${langMap.es}/${citySlug}/`);
  } else {
    const pathWithoutLang = path.replace(/^\/(es|en|pt|it)/, ''); // elimina idioma
    langs.forEach((lang) => {
      const href = `${baseUrl}/${lang}${pathWithoutLang}`;
      addHreflangTag(lang, href);
    });

    addHreflangTag('x-default', `${baseUrl}/en${pathWithoutLang}`);
  }
}

// 🧠 Función principal: seteo completo de head
function setHeadAttributes(lang, name, path) {
  const metaTags = getMetaDescriptions(lang, name, path);
  document.title = metaTags.title;

  // 🔄 Descripción y OG
  document.querySelectorAll("meta[name='description'], meta[property='og:description']").forEach((el) =>
    el.remove());

  metaTags.meta.forEach((tagDef) => {
    const tag = document.createElement('meta');
    Object.keys(tagDef).forEach((key) => tag.setAttribute(key, tagDef[key]));
    document.head.appendChild(tag);
  });

  // 🧹 Social meta tags (Open Graph + Twitter)
  const propertiesToRemove = [
    "meta[property='og:title']",
    "meta[property='og:description']",
    "meta[property='og:image']",
    "meta[property='og:url']",
    "meta[property='og:type']",
    "meta[property='og:site_name']",
    "meta[name='twitter:title']",
    "meta[name='twitter:description']",
    "meta[name='twitter:image']",
    "meta[name='twitter:card']",
    "meta[name='twitter:site']",
  ];

  propertiesToRemove.forEach((selector) => {
    const existing = document.querySelector(selector);
    if (existing) existing.remove();
  });

  const ogTitle = document.title;
  let ogDescription = '';
  const metaDescriptionTag = metaTags.meta.find((m) => m.name === 'description');
  if (metaDescriptionTag) {
    ogDescription = metaDescriptionTag.content;
  }
  const ogImage = 'https://wekeep.app/img/logo_wekeep.png';
  const ogUrl = `${baseUrl}${path}`;
  const siteName = 'WeKeep';

  const socialTags = [
    { property: 'og:title', content: ogTitle },
    { property: 'og:description', content: ogDescription },
    { property: 'og:image', content: ogImage },
    { property: 'og:url', content: ogUrl },
    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: siteName },
    { name: 'twitter:title', content: ogTitle },
    { name: 'twitter:description', content: ogDescription },
    { name: 'twitter:image', content: ogImage },
    { name: 'twitter:card', content: 'summary_large_image' },
    // { name: 'twitter:site', content: '@wekeepapp' }, // si tenés cuenta
  ];

  socialTags.forEach((tagDef) => {
    const tag = document.createElement('meta');
    Object.keys(tagDef).forEach((key) => tag.setAttribute(key, tagDef[key]));
    document.head.appendChild(tag);
  });

  // 🌐 Hreflang
  generateHreflangLinks(lang, path, name);

  // 🔗 Canonical
  const existingCanonical = document.querySelector("link[rel='canonical']");
  if (existingCanonical) existingCanonical.remove();

  const canonical = document.createElement('link');
  canonical.setAttribute('rel', 'canonical');
  canonical.setAttribute('href', `${baseUrl}${path}`);
  document.head.appendChild(canonical);

  // 🏢 Schema
  generateSchema(lang, name, path, ogTitle, ogDescription);
}

export { setHeadAttributes };
